class ConsentUpdateSubscription {
  /**
   * @type {Array.<[string, function, boolean]>}
   */
  consentListeners;

  /**
   * @type {Array.<[string, function]>}
   */
  documentListeners;

  /**
   * @type {boolean}
   */
  connected = false;

  constructor(consentListeners, documentListeners) {
    this.consentListeners = consentListeners;
    this.documentListeners = documentListeners;
  }

  connect() {
    if (this.connected) {
      return;
    }

    const cmp = getCmp();

    for (const consentListener of this.consentListeners) {
      cmp('addEventListener', consentListener, null);
    }

    for (const [ event, handler ] of this.documentListeners) {
      document.addEventListener(event, handler);
    }
  }

  disconnect() {
    if (this.connected === false) {
      return;
    }

    const cmp = getCmp();

    for (const consentListener of this.consentListeners) {
      cmp('removeEventListener', consentListener, null);
    }
  }
}

function hasProperty(object, property) {
  return Object.prototype.hasOwnProperty.call(object, property);
}

function getCmp() {
  if (hasProperty(window, '__cmp') === false || typeof window.__cmp !== "function") {
    return null;
  }

  return window.__cmp;
}

/**
 * @param {string} vendor
 * @param {(hasConsent: boolean) => void} callback
 * @param {boolean} sendInitialEvent
 * @returns {ConsentUpdateSubscription|null}
 */
export function watchVendorConsentUpdate(vendor, callback, sendInitialEvent = true) {
  if (callback == null || typeof callback !== 'function') {
    console.warn('Unable to register consent update for vendor', vendor, 'invalid callback');

    return null;
  }

  let lastConsentStatus = null;

  function sendConsentUpdate() {
    hasVendorConsent(vendor)
      .then((hasConsent) => {
        if (lastConsentStatus !== hasConsent) {
          lastConsentStatus = hasConsent;

          callback(hasConsent);
        }
      })
      .catch(() => callback(false));
  }

  const subscription = new ConsentUpdateSubscription([
    ['consent', sendConsentUpdate, false],
    ['consentapproved', sendConsentUpdate, false],
    ['consentrejected', sendConsentUpdate, false],
    ['vendorconsent', sendConsentUpdate, false],
  ], [
    [ 'consent-update', sendConsentUpdate ],
  ]);

  subscription.connect();

  if (sendInitialEvent) {
    sendConsentUpdate();
  }

  return subscription;
}

/**
 * @param {string} vendor
 * @returns {Promise<boolean|null>}
 */
export function hasVendorConsent(vendor) {
  return new Promise(function(resolve, reject) {
    const cmp = getCmp();
    if (cmp == null) {
      return reject('Could not find CMP, no consent possible.');
    }

    const consentData = cmp('getCMPData');
    if (false === hasProperty(consentData, 'vendorConsents')) {
      return resolve(false);
    }

    const { vendorConsents } = consentData;

    resolve(hasProperty(vendorConsents, vendor) && true === vendorConsents[vendor]);
  });
}

/**
 * @param {string} vendor
 * @param {number} consent
 * @return {Promise<void>}
 */
export function setVendorConsent(vendor, consent) {
  return new Promise((resolve, reject) => {
    const cmp = getCmp();
    if (cmp == null) {
      return reject('Could not find CMP, no consent possible.');
    }

    const result = cmp('setVendorConsent', [ vendor, consent ]);

    triggerConsentUpdate();

    resolve(result);
  });
}

export function getVendorName(vendor, unknownVendorName = 'Unbekannter Anbieter') {
  return new Promise((resolve, reject) => {
    const cmp = getCmp();
    if (cmp == null) {
      return reject('Could not find CMP, no consent possible.');
    }

    const consentData = cmp('getCMPData');
    if (false === hasProperty(consentData, 'vendorsList')) {
      return resolve(unknownVendorName);
    }

    const { vendorsList } = consentData;
    const vendorName = vendorsList.find(({ id }) => id === vendor);

    if (vendorName === null) {
      return resolve(unknownVendorName);
    }

    resolve(vendorName.name);
  });
}

export function triggerConsentUpdate() {
  document.dispatchEvent(new CustomEvent('consent-update'));
}
